// ============================================================================
// PV-1052 · M8 Patch 5b — public seller submission contract (front mirror)
// Refs: Spec V1.1 §M8 L1132-1250 · Modele V1.2 §4.9 · ADR-014 (building)
//       UI Behavior V1.1 §6.3 · Matrice V1.1 §M8 L291-298
//
// MIRROR of supabase/functions/seller-submission-create/index.ts (Patch 5a).
// The Edge re-validates with its own Zod copy — this front mirror exists so
// the form catches CHECK violations (E.164, asking_price >= 50000, off_plan
// conditional fields) BEFORE the network roundtrip and so messages can be
// localised via i18n on top of the canonical Zod error codes.
//
// Stacks are isolated (Vite/tsc here vs Deno + jsr there). Single source of
// truth = the Spec V1.1 itself, not either of the two Zod copies. Whenever
// the Edge contract changes, this file MUST be updated and the Vitest suite
// `sell-submission-schema.test.ts` MUST be kept in sync.
// ============================================================================

import { z } from 'zod';

// --- Canonical option keys -------------------------------------------------

export const REASON_KEYS = ['leaving', 'financial', 'quick', 'other'] as const;
export type ReasonKey = (typeof REASON_KEYS)[number];

export const BEDROOMS_KEYS = ['0', '1', '2', '3', '4'] as const;
export type BedroomsKey = (typeof BEDROOMS_KEYS)[number];

// `Leaving | Financial | Quick | Other` — Edge enum (Spec §M8 L1177)
export const REASON_EDGE_VALUES = ['Leaving', 'Financial', 'Quick', 'Other'] as const;
export type ReasonEdge = (typeof REASON_EDGE_VALUES)[number];

const REASON_KEY_TO_EDGE: Record<ReasonKey, ReasonEdge> = {
  leaving: 'Leaving',
  financial: 'Financial',
  quick: 'Quick',
  other: 'Other',
};

// --- Payload schema (mirror Edge index.ts L70-120) -------------------------

// Bound errors carry short canonical codes (e.g. `asking_too_low`) instead of
// Zod's English defaults — the front maps them to localised i18n messages and
// would otherwise have to pattern-match brittle prose like `Number must be
// greater than or equal to 50000`. The Edge schema in
// `supabase/functions/seller-submission-create/index.ts` re-validates with its
// own copy and returns its own messages; this front-side codepath only fires
// when `mapFormToPayload` rejects before the network roundtrip.
const basePayloadSchema = z.object({
  type: z.enum(['ready', 'off_plan']),
  seller_full_name: z.string().trim().min(2, 'name_too_short').max(140, 'name_too_long'),
  seller_email: z.string().trim().toLowerCase().email('email_invalid').max(254, 'email_invalid'),
  seller_phone: z
    .string()
    .trim()
    .regex(/^\+[1-9]\d{1,14}$/, 'invalid_e164'),
  zone: z.string().trim().min(1, 'zone_required').max(80, 'zone_too_long'),
  community: z.string().trim().min(1, 'community_required').max(100, 'community_too_long'),
  // Required since Patch 6 (ADR-024) — captured at the public submit, used by
  // accept_seller_submission RPC to auto-compose deals.title.
  building: z.string().trim().min(2, 'building_required').max(100, 'building_too_long'),
  bedrooms: z.number().int().min(0, 'bedrooms_out_of_range').max(10, 'bedrooms_out_of_range'),
  // Required since Patch 6 (ADR-024) — needed for deals.area_sqft NOT NULL on
  // the Draft created at acceptance.
  area_sqft: z.number().int().min(100, 'area_out_of_range').max(50000, 'area_out_of_range'),
  desired_asking_price: z.number().min(50000, 'asking_too_low'),
  original_price: z.number().min(50000, 'original_too_low').optional(),
  amount_paid: z.number().min(0, 'amount_paid_negative').optional(),
  reason_for_selling: z.enum(REASON_EDGE_VALUES).optional(),
  description: z.string().max(1500, 'description_too_long').optional(),
  // Locale captured from i18n.language at the public submit (Patch 6 — ADR-024).
  // Used by the accept_seller_submission RPC to localize the acceptance email.
  preferred_language: z.enum(['en', 'fr', 'ar']),
});

export const sellSubmissionPayloadSchema = basePayloadSchema.superRefine((data, ctx) => {
  if (data.type === 'off_plan') {
    if (data.original_price === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['original_price'],
        message: 'required_off_plan',
      });
    }
    if (data.amount_paid === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['amount_paid'],
        message: 'required_off_plan',
      });
    }
    if (
      data.original_price !== undefined &&
      data.amount_paid !== undefined &&
      data.amount_paid > data.original_price
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['amount_paid'],
        message: 'amount_exceeds_original',
      });
    }
  }
});

export type SellSubmissionPayload = z.infer<typeof sellSubmissionPayloadSchema>;

// --- Form-side types -------------------------------------------------------

// The form keeps everything as string (HTML <input> default). Numeric and
// enum coercions happen in `mapFormToPayload` so the form Zod schema stays
// a thin wrapper around what the user actually types.
export type SellFormValues = {
  fullName: string;
  email: string;
  phone: string;
  zone: string;
  community: string;
  building: string;
  area: string;
  bedrooms: string;
  askingPrice: string;
  originalPrice: string;
  amountPaid: string;
  reason: string;
  description: string;
};

export type SellType = 'ready' | 'offPlan';

// --- Mappers ---------------------------------------------------------------

export function mapTypeToEdge(type: SellType): SellSubmissionPayload['type'] {
  return type === 'offPlan' ? 'off_plan' : 'ready';
}

export function mapBedroomsToInt(value: string): number | undefined {
  if (!BEDROOMS_KEYS.includes(value as BedroomsKey)) return undefined;
  return Number.parseInt(value, 10);
}

export function mapReasonToEnum(value: string): ReasonEdge | undefined {
  if (!value) return undefined;
  if (!REASON_KEYS.includes(value as ReasonKey)) return undefined;
  return REASON_KEY_TO_EDGE[value as ReasonKey];
}

// Parse a numeric string into a finite Number, or undefined if blank.
// Returns null when the input is non-blank but unparseable — the caller
// surfaces that as a Zod error on the field path.
function parseOptionalNumber(value: string): number | undefined | null {
  const trimmed = value.trim();
  if (trimmed === '') return undefined;
  const cleaned = trimmed.replace(/[\s,]/g, '');
  const parsed = Number.parseFloat(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
}

function parseOptionalInt(value: string): number | undefined | null {
  const n = parseOptionalNumber(value);
  if (n === undefined || n === null) return n;
  return Number.isInteger(n) ? n : null;
}

// --- mapFormToPayload ------------------------------------------------------

export type MapFormResult =
  | { ok: true; payload: SellSubmissionPayload }
  | { ok: false; issues: z.ZodIssue[] };

/**
 * Turns SellFormValues + the chosen SellType into a SellSubmissionPayload
 * validated by the Edge mirror schema. Returns the Zod issues so the caller
 * can map each `path` to a react-hook-form `setError` and i18n message.
 *
 * `preferredLanguage` is captured from i18n.language at the call site
 * (SellPage). The page normalises `fr-FR` → `fr` before calling.
 */
export function mapFormToPayload(
  values: SellFormValues,
  type: SellType,
  preferredLanguage: 'en' | 'fr' | 'ar' = 'en',
): MapFormResult {
  const askingNumber = parseOptionalNumber(values.askingPrice);
  const originalNumber = parseOptionalNumber(values.originalPrice);
  const amountPaidNumber = parseOptionalNumber(values.amountPaid);
  const areaInt = parseOptionalInt(values.area);

  // Local issues for non-numeric strings supplied where numbers are expected.
  // Zod cannot detect those itself because the source is `unknown`.
  const localIssues: z.ZodIssue[] = [];
  if (askingNumber === null) {
    localIssues.push({
      code: z.ZodIssueCode.custom,
      path: ['askingPrice'],
      message: 'not_a_number',
    });
  }
  if (originalNumber === null) {
    localIssues.push({
      code: z.ZodIssueCode.custom,
      path: ['originalPrice'],
      message: 'not_a_number',
    });
  }
  if (amountPaidNumber === null) {
    localIssues.push({
      code: z.ZodIssueCode.custom,
      path: ['amountPaid'],
      message: 'not_a_number',
    });
  }
  if (areaInt === null) {
    localIssues.push({
      code: z.ZodIssueCode.custom,
      path: ['area'],
      message: 'not_an_integer',
    });
  }

  const bedroomsInt = mapBedroomsToInt(values.bedrooms);
  if (bedroomsInt === undefined) {
    localIssues.push({
      code: z.ZodIssueCode.custom,
      path: ['bedrooms'],
      message: 'invalid_bedrooms',
    });
  }

  if (localIssues.length > 0) {
    return { ok: false, issues: localIssues };
  }

  const candidate: Record<string, unknown> = {
    type: mapTypeToEdge(type),
    seller_full_name: values.fullName.trim(),
    seller_email: values.email.trim().toLowerCase(),
    seller_phone: values.phone.trim().replace(/\s+/g, ''),
    zone: values.zone.trim(),
    community: values.community.trim(),
    building: values.building.trim(), // required since Patch 6 — Zod will reject empty
    bedrooms: bedroomsInt,
    desired_asking_price: askingNumber,
    preferred_language: preferredLanguage,
  };

  // area_sqft is required since Patch 6 (ADR-024). Omitting the property when
  // blank lets Zod surface a missing-required error on `area_sqft`, which the
  // form maps to the `area` field via edgePathToFormField.
  if (areaInt !== undefined) candidate.area_sqft = areaInt;
  if (originalNumber !== undefined) candidate.original_price = originalNumber;
  if (amountPaidNumber !== undefined) candidate.amount_paid = amountPaidNumber;

  const reasonEnum = mapReasonToEnum(values.reason);
  if (reasonEnum !== undefined) candidate.reason_for_selling = reasonEnum;

  const descTrim = values.description.trim();
  if (descTrim !== '') candidate.description = descTrim;

  const parsed = sellSubmissionPayloadSchema.safeParse(candidate);
  if (!parsed.success) {
    return { ok: false, issues: parsed.error.issues };
  }
  return { ok: true, payload: parsed.data };
}

// --- Field-path → form-field mapping ---------------------------------------

// The Edge speaks snake_case payload paths; the form speaks camelCase. This
// table powers `setError` after a 400 invalid_payload response.
const EDGE_PATH_TO_FORM_FIELD: Readonly<Record<string, keyof SellFormValues>> = {
  seller_full_name: 'fullName',
  seller_email: 'email',
  seller_phone: 'phone',
  zone: 'zone',
  community: 'community',
  building: 'building',
  bedrooms: 'bedrooms',
  area_sqft: 'area',
  desired_asking_price: 'askingPrice',
  original_price: 'originalPrice',
  amount_paid: 'amountPaid',
  reason_for_selling: 'reason',
  description: 'description',
};

const FORM_FIELD_KEYS: ReadonlySet<string> = new Set<keyof SellFormValues>([
  'fullName',
  'email',
  'phone',
  'zone',
  'community',
  'building',
  'area',
  'bedrooms',
  'askingPrice',
  'originalPrice',
  'amountPaid',
  'reason',
  'description',
]);

export function edgePathToFormField(path: string): keyof SellFormValues | undefined {
  if (path in EDGE_PATH_TO_FORM_FIELD) return EDGE_PATH_TO_FORM_FIELD[path];
  // Front-side mapFormToPayload issues already use form-field paths.
  if (FORM_FIELD_KEYS.has(path)) return path as keyof SellFormValues;
  return undefined;
}
